import React from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { formValueSelector } from 'redux-form'
import { NewExternalSystem } from '@cbgms/api/modules/shops/external-systems'
import { isRequired } from '@cbgms/base/utils/validations'
import Dropdown, { DropdownOption } from '@cbgms/components/form/Material/Dropdown'
import Input from '@cbgms/components/form/Material/Input'
import Checkbox from '@cbgms/components/form/Material/Checkbox'
import { getExternalSystemValues } from './state/actions'
import { IRootReducerState } from 'state/reducers'
import FormLayout from 'core/page/layout/FormLayout'
import { Grid, Paper, Typography } from '@mui/material'
import { Loader } from '@carsys/ui/src/Loader'

interface ExternalSystemFormProps {
  form: string
  onSubmit: (values: NewExternalSystem) => void
  initialValues?: NewExternalSystem
  onSubmitSuccess: () => void
  redirectToURL: string
}

const ExternalSystemForm: React.FC<ExternalSystemFormProps> = ({ form, onSubmit, initialValues, onSubmitSuccess, redirectToURL }) => {
  const { t } = useTranslation()

  const [externalSystemOptions, setExternalSystemOptions] = React.useState<DropdownOption[] | null>(null)
  const dispatch = useDispatch()
  const currentExternalSystem = useSelector((state: IRootReducerState) => formValueSelector(form)(state, 'ExternalSystem'))
  const isMijnGrossier = currentExternalSystem === 'Mijngrossier'

  React.useEffect(() => {
    const asyncFunc = async () => {
      try {
        const response = await dispatch(getExternalSystemValues())
        const data = response.Data
        const options = data.map(value => ({ text: value, value, key: value }))
        setExternalSystemOptions(options)
      } catch {
        setExternalSystemOptions([{ text: 'sup', value: 'sup', key: 'sup' }])
      }
    }
    asyncFunc()
  }, [])

  if (!externalSystemOptions) return <Loader loading />

  return (
    <FormLayout
      form={form}
      onSubmit={onSubmit}
      initialValues={initialValues}
      onSubmitSuccess={onSubmitSuccess}
      redirectToURL={redirectToURL}
    >
      <Paper sx={{ mt: 1, p: 1 }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant='h5'>{t('external-system-form.header')}</Typography>
          </Grid>
          <Grid item xs={4}>
            <Dropdown
              fullWidth
              data-testid='dropdown-external-system'
              validate={isRequired}
              required
              label={t('external-system-form.external-systems')}
              name='ExternalSystem'
              options={externalSystemOptions}
            />
          </Grid>
          <Grid item xs={4}>
            <Input fullWidth name='SystemID' required label={t('external-system-form.system-id')} validate={isRequired} />
          </Grid>
          {isMijnGrossier && (
            <Grid item xs={4}>
              <Checkbox
                color='default'
                name='AdditionalConfig.MijnGrossier.ShouldRedirect'
                label={t('external-system-form.mijn_grossier_should_redirect')}
              />
            </Grid>
          )}
        </Grid>
      </Paper>
    </FormLayout>
  )
}

export default ExternalSystemForm
